import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getRfqs, normalizeRfqsList } from '@/services/demaxtoreApi';
import { getCommodityBidMe, getCommodityBidProductionItems } from '@/services/commoditybidIntegration';
import { normalizePlatformAccess } from '@/lib/utils';
import {
  isNotificationSoundEnabled,
  playNotificationChime,
  primeNotificationAudio,
} from '@/lib/notificationSound';

const SEEN_STORAGE_KEY = 'demaxtore_notification_seen_v1';
const POLL_MS = 30_000;
const MAX_ITEMS = 50;

const NotificationContext = createContext(null);

function loadSeenSet() {
  try {
    const raw = sessionStorage.getItem(SEEN_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function persistSeenSet(set) {
  try {
    sessionStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...set].slice(-500)));
  } catch {
    /* ignore quota */
  }
}

function activityKey(type, entityId) {
  return `${type}:${String(entityId)}`;
}

function formatNotification({ type, entityId, title, createdAt, message }) {
  const href = type === 'commoditybid' ? '/commoditybid/live-bids' : '/demaxtore/rfqs';
  const label =
    type === 'commoditybid'
      ? 'CommodityBid'
      : 'DeMaxtore RFQ';
  return {
    id: activityKey(type, entityId),
    type,
    entityId: String(entityId),
    title: title || (type === 'commoditybid' ? 'New production request' : 'New RFQ'),
    message:
      message ||
      (type === 'commoditybid'
        ? 'Your bid request is on the dashboard.'
        : 'Your RFQ was received.'),
    platformLabel: label,
    createdAt: createdAt || new Date().toISOString(),
    href,
    read: false,
  };
}

function rfqEntityId(rfq) {
  return rfq?.id ?? rfq?._id ?? rfq?.reference ?? '';
}

function collectRfqNotifications(rfqs) {
  return rfqs
    .map((rfq) => {
      const entityId = rfqEntityId(rfq);
      if (!entityId) return null;
      return formatNotification({
        type: 'rfq',
        entityId,
        title: rfq.title,
        createdAt: rfq.created_at ?? rfq.createdAt,
      });
    })
    .filter(Boolean);
}

function collectCommodityBidNotifications(payload) {
  return getCommodityBidProductionItems(payload).map((item) =>
      formatNotification({
        type: 'commoditybid',
        entityId: item.id,
        title: item.title,
        createdAt: item.createdAt,
        message: 'Your CommodityBid request is live.',
      }),
    );
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const platform = normalizePlatformAccess(user);
  const seenRef = useRef(loadSeenSet());
  const seededRef = useRef(false);
  const [notifications, setNotifications] = useState([]);
  const [polling, setPolling] = useState(false);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const mergeIncoming = useCallback((incoming, { playSound }) => {
    if (!incoming.length) return;

    const fresh = [];
    for (const n of incoming) {
      if (seenRef.current.has(n.id)) continue;
      seenRef.current.add(n.id);
      fresh.push(n);
    }
    persistSeenSet(seenRef.current);

    if (!fresh.length) return;

    if (playSound) playNotificationChime();

    setNotifications((prev) => {
      const byId = new Map(prev.map((p) => [p.id, p]));
      for (const n of fresh) {
        byId.set(n.id, { ...n, read: false });
      }
      return [...byId.values()]
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
        .slice(0, MAX_ITEMS);
    });
  }, []);

  const scanSources = useCallback(
    async ({ playSound }) => {
      const tasks = [];
      if (platform.demaxtore) {
        tasks.push(
          getRfqs()
            .then((res) => collectRfqNotifications(normalizeRfqsList(res.data)))
            .catch(() => []),
        );
      }
      if (platform.commoditybid) {
        tasks.push(
          getCommodityBidMe()
            .then((res) => collectCommodityBidNotifications(res.data))
            .catch(() => []),
        );
      }
      const batches = await Promise.all(tasks);
      mergeIncoming(batches.flat(), { playSound });
    },
    [mergeIncoming, platform.demaxtore, platform.commoditybid],
  );

  const refresh = useCallback(async () => {
    setPolling(true);
    try {
      await scanSources({ playSound: seededRef.current });
    } finally {
      setPolling(false);
    }
    seededRef.current = true;
  }, [scanSources]);

  const registerActivity = useCallback(
    (payload) => {
      const { type, entityId, title, createdAt, message, playSound = true } = payload;
      if (!entityId) return;
      const n = formatNotification({ type, entityId, title, createdAt, message });
      mergeIncoming([n], { playSound });
    },
    [mergeIncoming],
  );

  const markRead = useCallback((id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  useEffect(() => {
    if (!user?.role || user.role !== 'customer') return undefined;

    const onGesture = () => primeNotificationAudio();
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });

    seededRef.current = false;
    refresh();

    const timer = window.setInterval(() => refresh(), POLL_MS);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, [user?.role, user?._id, refresh]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      polling,
      refresh,
      registerActivity,
      markRead,
      markAllRead,
      isNotificationSoundEnabled,
    }),
    [notifications, unreadCount, polling, refresh, registerActivity, markRead, markAllRead],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx;
}
